import { ExpandableTable } from '@avanta/shared/components/ExpandableTable';
import { Text } from '@avanta/shared/components/Text';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { formatDateSimple } from '@avanta/shared/utils/date';
import { type ColumnDef } from '@tanstack/react-table';
import { FolderTree } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { EMPTY_VALUE } from 'constants/common';
import { useFetchAppointmentsHistory } from 'hooks/useFetchAppointmentsHistory';
import { useFetchPatientRecords } from 'hooks/useFetchPatientRecords';
import {
  getRecordStatusClassName,
  getRecordStatusLabel,
  getSickLeaveStartDate,
} from 'pages/PatientProfile/utils';
import { type Appointment } from 'types/appointments';
import { type PatientRecord } from 'types/patient';
import { AssociatedSessions } from './AssociatedSessions';

export const RecordsHistory = (): React.ReactElement => {
  const params = useParams();
  const patientId = params?.id ?? '';
  const { data: records = [], isLoading: isLoadingRecords } = useFetchPatientRecords(patientId);
  const { data: appointments = [], isLoading: isLoadingAppointments } =
    useFetchAppointmentsHistory(patientId);
  const isLoading = isLoadingRecords || isLoadingAppointments;
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);

  const columns = useMemo<ColumnDef<PatientRecord>[]>(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        enableSorting: true,
        header: (): string => t('PatientRecords.fields.code'),
        cell: ({ row }): string => row.original.code,
      },
      {
        id: 'start',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.start'),
        cell: ({ row }): string => getSickLeaveStartDate(row.original),
      },
      {
        id: 'end',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.end'),
        cell: ({ row }): string =>
          row.original.closedAt ? formatDateSimple(row.original.closedAt) : EMPTY_VALUE,
      },
      {
        id: 'contract',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.contractShort'),
        cell: ({ row }): string => row.original.contract?.code ?? EMPTY_VALUE,
      },
      {
        id: 'status',
        enableSorting: false,
        header: (): string => t('PatientRecords.fields.status'),
        cell: ({ row }): React.ReactElement => (
          <Text
            variant="text-sm-medium"
            className={cn(getRecordStatusClassName(row.original.status))}
          >
            {getRecordStatusLabel(row.original.status)}
          </Text>
        ),
      },
    ],
    [t]
  );

  const getAssociatedSessions = (record: PatientRecord): Appointment[] =>
    appointments.filter(appointment => appointment.contractRecordId === record.id);

  return (
    <Card
      variant="primary"
      title={t('PatientRecords.history.heading')}
      icon={FolderTree}
      className="am-md:px-4 am-md:pt-0.5"
    >
      <ExpandableTable
        data={records}
        columns={columns}
        getRowId={row => String(row.id)}
        isLoading={isLoading}
        emptyTitle={t('PatientRecords.history.empty.title')}
        emptyMessage={t('PatientRecords.history.empty.description')}
        expandAriaLabel={t('PatientRecords.history.expand')}
        collapseAriaLabel={t('PatientRecords.history.collapse')}
        renderExpandedContent={record => (
          <AssociatedSessions sessions={getAssociatedSessions(record)} />
        )}
      />
    </Card>
  );
};
